import type { OrgNode } from './org-context-value';

/**
 * Depth-first, parents before children, so indentation reads as ownership.
 *
 * Unchanged since `OrgSelector.tsx` — see the file comment for why `AccessView.tsx` cites it by
 * name. Its own file since 2026-10-14, when Onboarding's Organisation box needed the same order
 * as the header's Organisation column (`modules/onboarding/site-owner.ts`).
 */
export function orderedOrgs(nodes: readonly OrgNode[]): Array<{ node: OrgNode; depth: number }> {
  const byParent = new Map<string | null, OrgNode[]>();
  for (const n of nodes) {
    const key = n.parentId;
    const list = byParent.get(key);
    if (list) list.push(n);
    else byParent.set(key, [n]);
  }

  /*
   * A node whose parent is NOT in the reach is a root AS FAR AS THIS CALLER IS CONCERNED — the
   * normal case for a child organisation whose administrator signs in: it reaches itself and its
   * own descendants, never upward, so its `parentId` names something it cannot see. Treating
   * those as roots is what keeps the list from silently dropping them.
   */
  const ids = new Set(nodes.map((n) => n.id));
  const roots = nodes.filter((n) => n.parentId === null || !ids.has(n.parentId));

  const out: Array<{ node: OrgNode; depth: number }> = [];
  const walk = (node: OrgNode, depth: number): void => {
    out.push({ node, depth });
    for (const child of byParent.get(node.id) ?? []) walk(child, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  return out;
}
